import React, { useEffect, forwardRef, useImperativeHandle } from "react";
import "./SendStopButton.css";
import generateIcon from "../../assets/new-images/generate-icon.svg";
import micIcon from "../../assets/new-images/microphone-icon.svg";
import redMicIcon from "../../assets/new-images/red-mic.svg";
import useVoiceUtils from "../../hooks/useVoiceUtils";
import { playMicToggleSound } from "../../utils/audioUtils";
import { useEventEmitter } from "../../contexts/EventEmmiter";

/**
 * SendStopButton - A reusable component for send/stop button functionality
 * @param {boolean} showStopStreaming - If true, shows stop button instead of send button
 * @param {boolean} isLoading - Alternative way to control button state
 * @param {boolean} isSendButtonDisabled - Disables the send button
 * @param {function} handleSendMessage - Callback function for send button click
 * @param {function} handleStopStreaming - Callback function for stop button click
 * @param {string} sendLabel - Optional label text for send button
 * @param {boolean} showVoiceInput - If true, shows microphone button when there is no text typed
 * @param {boolean} hasText - Whether the input box currently has text
 * @param {function} onTranscript - Callback to receive voice transcript
 * @param {string} wsVoiceUrl - Optional WebSocket URL override for STT
 * @param {function} onKeyword - Called when a stop/pause keyword is detected in speech
 * @param {boolean} keepListeningWhileLoading - Keep mic active and visible during response generation
 */
const SendStopButton = forwardRef(({
    showStopStreaming,
    isLoading = false,
    isSendButtonDisabled = false,
    handleSendMessage,
    handleStopStreaming,
    sendLabel = null,
    showVoiceInput = false,
    hasText = false,
    onTranscript,
    theme = "default",
    onVoiceStart,
    onVoiceStop,
    onKeyword,
    keepListeningWhileLoading = false,
    isVoiceStopFeature = false,
    wsUrl = null,
    /** Auto-send delay in ms after silence (e.g. 3000). Null = disabled. */
    autoSendDelayMs = null,
    /** Called when the silence auto-send timer fires. Should trigger handleSendMessage. */
    onSilenceAutoSend = null,
}, ref) => {

    const {
        isListening,
        startListening,
        stopListening,
        toggleListening,
        status,
    } = useVoiceUtils({
        enabled: showVoiceInput,
        onTranscript,
        onKeyword,
        // Disable the short silence timeout when mic is running in background keyword-detection mode
        backgroundMode: keepListeningWhileLoading,
        isVoiceStopFeature,
        handleStopStreaming,
        wsUrl,
        autoSendDelayMs,
        onSilenceAutoSend,
    });

    const { emit } = useEventEmitter();

    useEffect(() => {
        emit("voiceInputStateChanged", isListening);
    }, [isListening, emit]);

    useImperativeHandle(ref, () => ({
        stopVoice: () => {
            if (isListening) {
                playMicToggleSound(false);
            }
            onVoiceStop?.();
            stopListening();
        },
        startVoice: () => {
            if (!isListening) {
                playMicToggleSound(true);
                onVoiceStart?.();
                toggleListening();
            }
        },
        /** Programmatic start without sound feedback (for background keep-listening). */
        startListening: () => {
            if (!isListening) startListening();
        },
        /** Programmatic stop without sound feedback. */
        stopListening: () => {
            stopListening();
        },
    }), [isListening, startListening, stopListening, toggleListening, onVoiceStart, onVoiceStop]);

    const isTalentPulse = theme === "talent-pulse";

    const handleVoiceClick = () => {
        // Fire the caller's side-effect callback first
        if (isListening) {
            playMicToggleSound(false);
            onVoiceStop?.();
        } else {
            playMicToggleSound(true);
            onVoiceStart?.();
        }
        // Always toggle the actual mic capture
        toggleListening();
    };

    const shouldShowStop =
        showStopStreaming !== undefined
            ? showStopStreaming
            : isLoading;

    const isDeepInsights = theme === "deep-insights";

    // Streaming stop button — skip if we want the mic visible while loading
    if (shouldShowStop && !keepListeningWhileLoading) {
        return (
            <button
                className={`stop-btn ${isTalentPulse ? "talent-pulse-theme" : ""} ${isDeepInsights ? "deep-insights-theme" : ""}`}
                onClick={handleStopStreaming || (() => { })}
                title="Stop"
                type="button"
            >
                <span className="stop-icon" />
            </button>
        );
    }

    // Voice input button — show when: voice enabled, no text OR listening (or connecting), or keep-listening mode
    const isVoiceActive = isListening || status === "connecting" || keepListeningWhileLoading;
    if (showVoiceInput && (!hasText || isVoiceActive)) {
        // In keep-listening mode the button should be clickable so the user can stop it manually.
        // Only disable when loading AND not in keep-listening mode.
        const isMicDisabled = isLoading && !keepListeningWhileLoading;
        return (
            <button
                className={`voice-input-btn ${isListening ? "listening" : ""} ${isTalentPulse ? "talent-pulse-theme" : ""} ${isDeepInsights ? "deep-insights-theme" : ""} ${isMicDisabled ? "disabled" : ""}`}
                onClick={handleVoiceClick}
                title={isListening ? "Stop listening" : keepListeningWhileLoading ? "Listening for commands (say 'stop' to stop)" : "Speak your prompt"}
                type="button"
                disabled={isMicDisabled}
            >
                {(isListening || status === "connecting" || keepListeningWhileLoading) ? (
                    <span className="stop-icon" />
                ) : (
                    <img
                        src={isDeepInsights ? redMicIcon : micIcon}
                        alt="microphone"
                    />
                )}

                <span>
                    {(isListening || status === "connecting" || keepListeningWhileLoading) ? "Stop" : "Voice"}
                </span>
            </button>
        );
    }

    // Send button
    return (
        <button
            className={`send-btn ${isTalentPulse ? "talent-pulse-theme" : ""} ${isSendButtonDisabled ? "disabled" : ""}`}
            onClick={handleSendMessage}
            disabled={isSendButtonDisabled}
            title={sendLabel || "Send"}
            type="button"
        >
            <img src={generateIcon} alt="generate" />
            {sendLabel && <span>{sendLabel}</span>}
        </button>
    );
});

export default SendStopButton;